// The three things a package can leave on the page — t-159 §4/§5 (waiting,
// collapsed and expanded), §7 (applied) and §8 (refused).
//
// Both tabs draw the same banner over the same package: the Settings tab
// expands it into the field diff, and the Rewards tab into the prize table
// (see `diff` below). Which is the point — one package, and neither tab may
// look like the board is holding one of its own.

import type { ReactNode } from "react";

import { AlertTriangle, Check, ChevronDown, ChevronUp, Clock } from "lucide-react";

import { Button } from "../../components/Button/Button";
import {
  cutEvent,
  formatMoment,
  formatStamp,
  formatUntil,
  type NextCut,
} from "./pendingWhen";
import { DiffBox } from "./pendingDiffView";
import type { DiffGroup } from "./pendingDiff";
import type { PendingPackage } from "./types";
import styles from "./pending.module.css";

function changeCount(n: number): string {
  return n === 1 ? "1 change" : `${n} changes`;
}

// --- Waiting, and refused ---------------------------------------------------

export interface PendingBannerProps {
  pkg: PendingPackage;
  /** The package as rows — built by the host, which is the side that knows the
     board and its prize table. */
  groups: DiffGroup[];
  /** When this package is applied, where the host knows it. */
  nextCut?: NextCut;
  expanded: boolean;
  onToggleExpanded: () => void;
  onCancelAll: () => void;
  onCancelField: (field: string, label: string) => void;
  busy?: boolean;
  /** Refused packages only: clear the trace, or put its fields back in the
     form as an unsaved edit. */
  onDismiss?: () => void;
  onReschedule?: () => void;
  /**
   * The diff body, where the host draws its own. One package, two tabs: the
   * Rewards tab collapses the board's fields to a single line and spells the
   * prize table out range by range, which is the same rows read the other way
   * round. `groups` is still what the banner counts — the title says how many
   * changes there are, not how they are drawn.
   */
  diff?: ReactNode;
}

export function PendingBanner({
  pkg,
  groups,
  nextCut,
  expanded,
  onToggleExpanded,
  onCancelAll,
  onCancelField,
  busy,
  onDismiss,
  onReschedule,
  diff,
}: PendingBannerProps) {
  const failed = pkg.status === "failed";
  const count = groups.reduce((n, g) => n + g.rows.length, 0);
  const cutMoment = formatMoment(failed ? pkg.resolved_at : nextCut?.at);
  const until = failed ? null : formatUntil(nextCut?.at);
  const scheduledAt = formatStamp(pkg.created_at);

  return (
    <div
      className={`${styles.pendingBanner} ${failed ? styles.failed : ""}`}
      role="status"
      /* Named so the page has one region to point at: three of the form's own
         cards share their heading with a group of the diff, and "Scoring"
         means two different things until this region has a name. */
      aria-label={failed ? "Refused changes" : "Scheduled changes"}
    >
      <div className={styles.pbIco}>
        {failed ? <AlertTriangle size={16} aria-hidden /> : <Clock size={16} aria-hidden />}
      </div>
      <div className={styles.pbBody}>
        <div className={styles.pbTitle}>
          {failed
            ? `${count} scheduled changes were not applied`
            : `${changeCount(count)} scheduled for the next cycle`}
        </div>
        {failed ? (
          <div className={styles.pbSub}>
            At the cut{cutMoment ? " on " : " "}
            {cutMoment && <b>{cutMoment}</b>} the board no longer accepted them.
            The cycle was cut on the configuration the board already had, and
            this will not be retried.
          </div>
        ) : (
          <div className={styles.pbSub}>
            {until && (
              <span className={styles.pbWhen}>
                <Clock size={12} aria-hidden />
                {until}
              </span>
            )}{" "}
            {cutMoment && <>{cutMoment} — when {cutEvent(nextCut)} · </>}
            scheduled by <b>{pkg.actor_email}</b>
            {scheduledAt && <> on {scheduledAt}</>}
          </div>
        )}
        {failed && pkg.failure_reason && (
          <div className={styles.modalQuote}>{pkg.failure_reason}</div>
        )}
        {(expanded || failed) &&
          (diff ?? (
            <DiffBox
              groups={groups}
              variant={failed ? "muted" : "list"}
              onCancelField={failed ? undefined : onCancelField}
              cancelBusy={busy}
            />
          ))}
      </div>
      <div className={styles.pbActions}>
        {failed ? (
          <>
            <Button variant="ghost" size="sm" onClick={onDismiss} disabled={busy}>
              Dismiss
            </Button>
            <Button variant="secondary" size="sm" onClick={onReschedule} disabled={busy}>
              Edit and reschedule
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={onCancelAll} disabled={busy}>
              Cancel all
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={onToggleExpanded}
              icon={
                expanded ? (
                  <ChevronUp size={12} aria-hidden />
                ) : (
                  <ChevronDown size={12} aria-hidden />
                )
              }
            >
              {expanded ? "Hide" : "Review"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// --- Applied ----------------------------------------------------------------

export interface AppliedStripProps {
  pkg: PendingPackage;
  count: number;
  onDismiss: () => void;
  /** Where the audit entry is. A plain href rather than a callback: the tabs
     of this page are a query parameter, so the link is a real link and works
     without a router in the tree. */
  auditHref: string;
  busy?: boolean;
}

/**
 * The one line an applied package leaves behind. Nothing else marks it: the
 * values are on the board now, which is where an operator would look for them.
 */
export function AppliedStrip({
  pkg,
  count,
  onDismiss,
  auditHref,
  busy,
}: AppliedStripProps) {
  const at = formatMoment(pkg.resolved_at);
  return (
    <div className={styles.appliedStrip} role="status" aria-label="Applied changes">
      <div className={styles.asIco}>
        <Check size={15} aria-hidden />
      </div>
      <div className={styles.asBody}>
        <b>{count} scheduled changes applied</b>
        <div className={styles.asSub}>
          At the cut of the new cycle{at ? ` — ${at}` : ""}. The cycle that
          closed was ranked and paid out by the configuration it was cut under,
          not by this one.
        </div>
      </div>
      <a className={styles.auditLink} href={auditHref}>
        View in the audit log
      </a>
      <Button variant="ghost" size="sm" onClick={onDismiss} disabled={busy}>
        Dismiss
      </Button>
    </div>
  );
}
